import {
  Directive,
  OnDestroy,
  Renderer2,
  Inject,
  Optional,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CaptchaService } from './captcha.service';
import { WhitelabelService } from './whitelabel.service';
import { environment } from 'src/environments/environment';

/**
 * Base class for components that need CAPTCHA functionality.
 * Provides common CAPTCHA properties, validation logic, and lifecycle management.
 *
 * @example
 * export class MyComponent extends CaptchaBaseComponent<MyFormData> implements OnInit {
 *   constructor(
 *     captchaService: CaptchaService,
 *     @Inject(DOCUMENT) document: Document,
 *     renderer: Renderer2
 *   ) {
 *     super(captchaService, document, renderer);
 *   }
 *
 *   onSubmit(formData: MyFormData) {
 *     this.checkCaptchaAndProceed(formData, (data, token) => {
 *       // Your submission logic here
 *       this.myService.submit(data, token);
 *     });
 *   }
 * }
 */
@Directive()
export abstract class CaptchaBaseComponent<T = any> implements OnDestroy {
  // CAPTCHA UI state
  showCaptchaChallenge = false;
  captchaSiteKey = environment.recaptchaSiteKey;
  captchaToken: string | null = null;
  captchaError = false;

  // Stores form data while waiting for CAPTCHA verification
  protected pendingFormData: T | null = null;

  constructor(
    protected captchaService: CaptchaService,
    @Optional() @Inject(DOCUMENT) private document: Document | null = null,
    @Optional() private renderer: Renderer2 | null = null,
    @Optional() protected whitelabelService: WhitelabelService | null = null,
  ) {}

  /**
   * Checks if CAPTCHA token is valid and proceeds with submission.
   * If token is invalid, shows CAPTCHA challenge and stores form data for later.
   *
   * @param formData - The form data to submit
   * @param submitCallback - Callback function to execute when CAPTCHA is valid
   * @returns true if submission proceeded, false if CAPTCHA challenge was shown
   */
  protected checkCaptchaAndProceed(
    formData: T,
    submitCallback: (data: T, token: string) => void,
  ): boolean {
    // When recaptcha is disabled via tenant config (e.g. DEV), skip the
    // challenge and submit with an empty token.
    if (this.isCaptchaBypassed()) {
      submitCallback(formData, '');
      this.pendingFormData = null;
      return true;
    }

    const currentToken = this.captchaService.getCaptchaTokenValue();

    if (!currentToken || !this.captchaService.isTokenValid()) {
      // Store form data for later submission after CAPTCHA verification
      this.pendingFormData = formData;

      // Show CAPTCHA challenge
      this.showCaptchaChallenge = true;
      this.captchaService.setCaptchaRequired(true);
      this.captchaError = true;

      return false; // Don't proceed until CAPTCHA is completed
    }

    // CAPTCHA is valid, proceed with submission
    submitCallback(formData, currentToken);
    this.pendingFormData = null;

    // Reset CAPTCHA token after use to prevent reuse
    this.captchaService.resetCaptcha();

    return true;
  }

  /**
   * Called when CAPTCHA is successfully resolved.
   * Updates state and triggers pending form submission if available.
   */
  onCaptchaResolved(token: string): void {
    this.captchaToken = token;
    this.captchaError = false;
    this.showCaptchaChallenge = false;
    this.captchaService.setCaptchaToken(token);
    this.captchaService.setCaptchaRequired(false);

    // Automatically proceed with form submission after CAPTCHA is verified
    if (this.pendingFormData) {
      // Use setTimeout to defer execution to next tick, allowing UI to update
      setTimeout(() => {
        this.retryPendingSubmission();
      }, 0);
    }
  }

  /**
   * Override this method to define how to retry submission with pending form data.
   * This is called automatically after CAPTCHA is resolved.
   */
  protected abstract retryPendingSubmission(): void;

  /**
   * Called when CAPTCHA verification fails.
   */
  onCaptchaError(): void {
    this.captchaError = true;
    this.captchaToken = null;
  }

  /**
   * Called when CAPTCHA token expires.
   */
  onCaptchaExpired(): void {
    this.captchaError = true;
    this.captchaToken = null;
  }

  /**
   * Returns true when reCAPTCHA must be skipped.
   * Driven by the `recaptchaEnabled` flag from the current tenant config API.
   */
  protected isCaptchaBypassed(): boolean {
    return this.whitelabelService
      ? !this.whitelabelService.isRecaptchaEnabled()
      : false;
  }

  /**
   * Cleanup CAPTCHA state on component destruction.
   */
  ngOnDestroy(): void {
    this.cleanupCaptcha();
  }

  /**
   * Cleans up CAPTCHA state and removes reCAPTCHA badge from DOM.
   */
  protected cleanupCaptcha(): void {
    this.showCaptchaChallenge = false;
    this.captchaService.setCaptchaRequired(false);
    this.captchaService.resetCaptcha();
    this.captchaToken = null;
    this.captchaError = false;
    this.pendingFormData = null;
    this.removeRecaptchaBadge();
  }

  /**
   * Removes the reCAPTCHA badge from the DOM using Angular's Renderer2.
   * This is SSR-safe and follows Angular best practices.
   * This is useful for cleanup when the component is destroyed.
   */
  private removeRecaptchaBadge(): void {
    if (!this.document || !this.renderer) {
      // If Renderer2 or Document is not available, skip badge removal
      // This can happen in SSR or when the component is used in certain contexts
      return;
    }

    try {
      const badge = this.document.querySelector('.grecaptcha-badge');
      if (badge && badge.parentNode) {
        this.renderer.removeChild(badge.parentNode, badge);
      }
    } catch (error) {
      console.warn('Could not remove reCAPTCHA badge:', error);
    }
  }
}
